export const STATUSES: Record<string, { tone: string; label: string }> = {
    created: { tone: 'info', label: 'Authorizing' },
    pending: { tone: 'info', label: 'Awaiting Decision' },
    requires_payment_method: { tone: 'info', label: 'Authorizing' },
    requires_confirmation: { tone: 'info', label: 'Authorizing' },
    requires_customer_action: { tone: 'attention', label: 'Action Required' },
    requires_capture: { tone: 'warning', label: 'Authorized' },
    authorized: { tone: 'warning', label: 'Authorized' },
    processing: { tone: 'info', label: 'Authorizing' },
    captured: { tone: 'success', label: 'Invoiced' },
    succeeded: { tone: 'success', label: 'Invoiced' },
    failed: { tone: 'critical', label: 'Declined' },
    expired: { tone: 'critical', label: 'Expired' },
    cancelled: { tone: 'critical', label: 'Revoked' },
    // Non-connector outcomes — we never got a decision from the connector, so
    // these are NOT "Declined". Yellow (attention) — distinct from orange
    // (authorized/warning) and red (declined/critical). Retryable.
    incomplete: { tone: 'attention', label: 'Incomplete Data' },
    error: { tone: 'attention', label: "Couldn't process" },
}

export const CONFIRMABLE = ['created', 'requires_payment_method', 'requires_confirmation']
export const CAPTURABLE = ['requires_capture', 'authorized']
export const CANCELLABLE = [
    'created', 'pending', 'requires_payment_method', 'requires_confirmation',
    'requires_capture', 'authorized', 'processing',
]
export const TERMINAL_SUCCESS = ['captured', 'succeeded']
export const TERMINAL_FAILURE = ['failed', 'expired', 'cancelled']
// External contract: statuses at which an order is safe to ship. Consumed by
// systems syncing the order (e.g. BC) via the bnplx_status custom attribute.
// Every other status (incomplete/error/failed/pending/processing/…) = hold.
export const SHIPPABLE = [...TERMINAL_SUCCESS, 'authorized', 'requires_capture']
// States that should offer "Request Guarantee" again. Connector-terminal
// failures + our own non-connector outcomes (incomplete data / BNPLX error),
// none of which hold a live payment worth resuming.
export const RECREATABLE = [...TERMINAL_FAILURE, 'incomplete', 'error']
export const POLLING_STATES = ['processing']
// In-progress states — the guarantee is actively being authorized. Show a spinner
// so the merchant sees it's working (not stuck) rather than a static badge.
export const AUTHORIZING = [...CONFIRMABLE, 'processing']

export function isConfirmable(status: string | null): boolean {
    return !!status && CONFIRMABLE.includes(status)
}
export function isCapturable(status: string | null): boolean {
    return !!status && CAPTURABLE.includes(status)
}
export function isCancellable(status: string | null): boolean {
    return !!status && CANCELLABLE.includes(status)
}
export function isTerminal(status: string | null): boolean {
    return !!status && [...TERMINAL_SUCCESS, ...TERMINAL_FAILURE].includes(status)
}
export function isRecreatable(status: string | null): boolean {
    return !!status && RECREATABLE.includes(status)
}
export function needsPolling(status: string | null): boolean {
    return !!status && POLLING_STATES.includes(status)
}
export function isAuthorizing(status: string | null): boolean {
    return !!status && AUTHORIZING.includes(status)
}
export function getStatusConfig(status: string | null) {
    if (!status) return { tone: 'subdued' as const, label: 'No Guarantee' }
    return STATUSES[status] || { tone: 'subdued' as const, label: status }
}

// External contract published as custom attributes for systems syncing the
// order (e.g. BC). Two orthogonal views derived from our internal status:
//   bnplx_guarantee_status — the authorization/guarantee lifecycle
//   bnplx_invoice_status    — the capture/invoice lifecycle
// These are the single switch points for the external value vocabulary.

// Authorization view. Includes our non-connector app states (incomplete/error)
// so one field tells BC both whether the guarantee is good and why if not.
export function toGuaranteeStatus(status: string | null): string {
    if (!status) return ''
    if (['authorized', 'requires_capture', 'captured', 'succeeded'].includes(status)) return 'authorized'
    if (status === 'failed') return 'declined'
    if (status === 'expired') return 'expired'
    if (status === 'cancelled') return 'cancelled'
    if (status === 'incomplete') return 'incomplete'
    if (status === 'error') return 'error'
    // created / requires_payment_method / requires_confirmation /
    // requires_customer_action / processing / pending
    return 'pending'
}

// Capture/invoice view. Only meaningful once a guarantee exists.
export function toInvoiceStatus(status: string | null): string {
    if (!status || ['incomplete', 'error'].includes(status)) return ''
    if (TERMINAL_SUCCESS.includes(status)) return 'invoiced'
    return 'not_invoiced'
}
